import { apiRequest } from "@/lib/api";
import { getAccessToken } from "@/lib/auth-storage";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export type UploadedAttachment = {
  id: string;
  ticketId: string;
  fileName: string;
  mimeType: string;
  storagePath: string;
  fileSize: number;
  tag: string;
  createdAt: string;
  url: string;
};

export async function uploadTicketAttachment(params: {
  ticketId: string;
  file: File;
  tag?: string;
  onProgress?: (progress: number) => void;
}) {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Not authenticated.");
  }

  const formData = new FormData();
  formData.append("file", params.file);
  if (params.tag) {
    formData.append("tag", params.tag);
  }

  return new Promise<UploadedAttachment>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/attachments/tickets/${params.ticketId}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !params.onProgress) return;
      const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
      params.onProgress(progress);
    };

    xhr.onerror = () => reject(new Error("Upload failed. Check your connection and retry."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = JSON.parse(xhr.responseText) as UploadedAttachment;
          resolve(parsed);
        } catch {
          reject(new Error("Upload response parse failed."));
        }
        return;
      }
      try {
        const payload = JSON.parse(xhr.responseText) as { message?: string | string[]; error?: string };
        const message = Array.isArray(payload.message)
          ? payload.message.join(", ")
          : payload.message || payload.error || `Upload failed (${xhr.status})`;
        reject(new Error(message));
      } catch {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };

    xhr.send(formData);
  });
}

export function deleteAttachment(attachmentId: string) {
  return apiRequest<{ success: boolean }>(`/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}
